/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { When } from "react-if";
import {
  Box,
  FlexContainer,
  Select,
  Switch,
  Text,
  theme,
} from "@nordcloud/gnui";
import { GranularitySelector, TermSelector } from "./components";
import { mapSwitcherGroupLabel } from "./helpers";
import { useHeaderParameters } from "./hooks";
import { Period, ChartOption, Granularity, DateRange } from "./types";

type Props = {
  period: Period;
  options: ChartOption[];
  chartType: ChartOption;
  range: DateRange | undefined;
  granularity: Granularity;
  disableDailyView: boolean;
  onChange: (item: ChartOption | null) => void;
  onSetRange: (dateRange?: DateRange) => void;
  onSetGranularity: (granularity: Granularity) => void;
  onHandleClick: (period: Period) => void;
  isOtherCostsSwitchVisible?: boolean;
  showOtherCosts?: boolean;
  isOtherCostsSwitchDisabled?: boolean;
  toggleOtherCosts?: () => void;
};

export function CostAnalysisChartHeader({
  period,
  options,
  chartType,
  range,
  granularity,
  disableDailyView,
  isOtherCostsSwitchVisible,
  showOtherCosts,
  isOtherCostsSwitchDisabled,
  onChange,
  onSetRange,
  onSetGranularity,
  onHandleClick,
  toggleOtherCosts,
}: Props) {
  const {
    queryRangeStartDate,
    queryRangeEndDate,
    handleTermChange,
    handleGranularityChange,
    handleChartChange,
  } = useHeaderParameters({
    showOtherCosts,
    onHandleClick,
    onRangeChange: onSetRange,
    onChartChange: onChange,
    onGranularityChange: onSetGranularity,
  });

  return (
    <>
      <Box boxStyle="lightGrey">
        <FlexContainer alignItems="center" wrap="wrap">
          <TermSelector
            period={period}
            range={range}
            rangeStartDate={queryRangeStartDate}
            rangeEndDate={queryRangeEndDate}
            onChange={handleTermChange}
          />
          <GranularitySelector
            granularity={granularity}
            disableDailyView={disableDailyView}
            onChange={handleGranularityChange}
          />
          <FlexContainer
            paddingBottom={theme.spacing.spacing01}
            marginRight={theme.spacing.spacing04}
          >
            <Text size="sm" mb="0" mr={theme.spacing.spacing04} weight="medium">
              Chart
            </Text>
            <Select
              name="ChartType"
              options={options}
              value={chartType}
              styles={{
                control: (base) => ({
                  ...base,
                  minWidth: "16rem",
                }),
              }}
              onChange={handleChartChange}
            />
          </FlexContainer>
          <When condition={isOtherCostsSwitchVisible}>
            <div
              css={{
                opacity: isOtherCostsSwitchDisabled ? 0.5 : 1,
              }}
            >
              <Switch
                checked={showOtherCosts}
                labelText={mapSwitcherGroupLabel(chartType.value)}
                position="right"
                id="switch-show-others"
                data-testid="switch-show-others"
                disabled={isOtherCostsSwitchDisabled}
                onChange={toggleOtherCosts}
              />
            </div>
          </When>
        </FlexContainer>
      </Box>
    </>
  );
}
